import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Star, TrendingUp, Shield, Zap, Building2 } from 'lucide-react';
import LogoSlider from '../components/LogoSlider';

const Home = () => {
  const stats = [
    { number: '70-90%', label: 'Improvement in cash flow', description: 'Through autonomous payment optimization' },
    { number: '95%', label: 'Reduction in manual tasks', description: 'Via intelligent automation' },
    { number: '15 days', label: 'Complete deployment', description: 'Enterprise platform setup' },
    { number: '99%', label: 'Compliance improvement', description: 'Audit-ready workflows' }
  ];

  const features = [
    {
      title: 'Agent-Driven Intelligence',
      description: 'AI agents make complex decisions without human intervention, managing unlimited parties in single transactions.',
      benefits: ['Autonomous Decision Making', 'Multi-Party Orchestration', 'Predictive Optimization', 'Real-Time Adaptation']
    },
    {
      title: 'Enterprise Financial Integration',
      description: 'Direct integration with global banking systems and instant credit decisions in 60 seconds vs. days.',
      benefits: ['Embedded Banking', 'Instant Credit Decisions', 'Multi-Party Financing', 'Automated Risk Management']
    },
    {
      title: 'Industry-Specific Solutions',
      description: 'Purpose-built solutions for construction, manufacturing, healthcare with automatic compliance.',
      benefits: ['Vertical Specialization', 'Regulatory Compliance', 'Professional Certification', 'Quality Assurance']
    }
  ];

  const industries = [
    { name: 'Manufacturing', description: '10B+ revenue OEMs', icon: TrendingUp },
    { name: 'Construction', description: 'Project orchestration', icon: Shield },
    { name: 'Financial Services', description: 'Top-10 institutions', icon: Zap },
    { name: 'Healthcare', description: 'Enterprise providers', icon: CheckCircle }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#854DFF] via-[#7A42E6] to-[#6F37CC] min-h-screen flex items-center overflow-hidden">
        {/* Geometric Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-2/3 h-full">
            <div 
              className="absolute top-0 right-0 w-full h-3/4 bg-gradient-to-bl from-purple-500/30 via-purple-400/20 to-purple-600/10"
              style={{
                clipPath: 'polygon(40% 0%, 100% 0%, 100% 100%, 0% 100%)'
              }}
            ></div>
          </div>
          <div className="absolute bottom-1/4 right-1/3 w-96 h-96">
            <div 
              className="absolute inset-0 bg-gradient-to-br from-purple-400/20 to-purple-500/10"
              style={{
                clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)'
              }}
            ></div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
            <div className="mb-12 lg:mb-0">
              <div className="mb-6">
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-[#854DFF]/20 text-[#854DFF] backdrop-blur-sm">
                  <Star className="w-4 h-4 mr-2" />
                  Enterprise-Grade Agent-Driven Commerce
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
                <span className="block">Transform</span>
                <span className="block">Enterprise</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#B19CFF] to-white">
                  Commerce
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-white mb-8 leading-relaxed max-w-2xl">
                The world's most advanced Agent-Driven Commerce Platform for OEMs, Financial Institutions, and Enterprise Organizations. Autonomous agents orchestrate complex multi-party deals at enterprise scale.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-white text-[#854DFF] hover:bg-gray-50 font-semibold text-lg px-8 py-4 rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
                >
                  Schedule Enterprise Demo
                  <ArrowRight className="ml-3 h-5 w-5" />
                </Link>
                <Link
                  to="/solutions"
                  className="border-2 border-white/30 text-white hover:bg-white/10 font-semibold text-lg px-8 py-4 rounded-lg transition-all duration-300 inline-flex items-center justify-center backdrop-blur-sm"
                >
                  Explore Solutions
                </Link>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="hidden lg:block">
              <div className="grid grid-cols-2 gap-6">
                {stats.slice(0, 4).map((stat, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
                    <div className="text-3xl font-bold text-white mb-2">{stat.number}</div>
                    <div className="text-purple-100 font-medium mb-1">{stat.label}</div>
                    <div className="text-purple-200 text-sm">{stat.description}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
              Trusted by Enterprise Leaders Across Industries
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {industries.map((industry, index) => {
                const IconComponent = industry.icon;
                return (
                  <div key={index} className="text-center">
                    <div className="mx-auto w-16 h-16 bg-gradient-to-br from-[#854DFF]/20 to-[#854DFF]/30 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="w-8 h-8 text-[#854DFF]" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1">{industry.name}</h3>
                    <p className="text-sm text-gray-600">{industry.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Additional Trust Indicators */}
          <div className="mt-16">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Enterprise Scale & Recognition</h3>
              <p className="text-gray-600 max-w-2xl mx-auto">Serving Fortune 500 companies worldwide with proven enterprise-grade reliability</p>
            </div>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#854DFF] mb-2">Fortune 500</div>
                <div className="text-gray-700">Enterprise Clients</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#854DFF] mb-2">4+</div>
                <div className="text-gray-700">Countries Served</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#854DFF] mb-2">$10M+</div>
                <div className="text-gray-700">Transaction Volume</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#854DFF] mb-2">24/7</div>
                <div className="text-gray-700">Global Support</div>
              </div>
            </div>
          </div>
          
          {/* Company Logos Slider */}
          <div className="mt-16">
            <LogoSlider />
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Revolutionary Capabilities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              CredFlexi doesn't just facilitate transactions—it revolutionizes how enterprises operate, compete, and grow through intelligent agent orchestration.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            {features.map((feature, index) => (
              <div key={index} className="">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">{feature.description}</p>
                </div>
                <ul className="space-y-3">
                  {feature.benefits.map((benefit, benefitIndex) => (
                    <li key={benefitIndex} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#854DFF] mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Impact */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-purple-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Measurable Enterprise Impact
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Proven results across enterprise deployments with quantifiable improvements.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 text-center">
                <div className="text-4xl font-bold text-[#854DFF] mb-3">{stat.number}</div>
                <div className="text-lg font-semibold text-gray-900 mb-2">{stat.label}</div>
                <div className="text-gray-600">{stat.description}</div>
              </div>
            ))}
          </div>
          
          {/* Additional Performance Metrics */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Financial Performance Revolution</h3>
              <p className="text-gray-600">Transforming enterprise financial operations through intelligent automation</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">50%</div>
                <div className="text-lg font-semibold text-gray-900 mb-2">Reduction in transaction costs</div>
                <div className="text-gray-600 text-sm">Through intelligent automation</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">60-120%</div>
                <div className="text-lg font-semibold text-gray-900 mb-2">Increase in revenue</div>
                <div className="text-gray-600 text-sm">Through AI-powered market expansion</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">40-60%</div>
                <div className="text-lg font-semibold text-gray-900 mb-2">Improvement in profit margins</div>
                <div className="text-gray-600 text-sm">Through cost optimization</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Success Stories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Customer Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real-world transformations from enterprise leaders across industries
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Case Study 1 */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-8">
              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mr-4">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Global OEM Manufacturer</h3>
                    <p className="text-gray-600 text-sm">$10M+ Annual Revenue</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  "CredFlexi transformed our distribution strategy with autonomous marketplace creation for 50,000+ SKUs across 200+ distributors in 40 countries."
                </p>
              </div>
              <div className="border-t border-blue-200 pt-4">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">300%</div>
                    <div className="text-xs text-gray-600">Faster onboarding</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">$5M</div>
                    <div className="text-xs text-gray-600">Additional revenue</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Case Study 2 */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-8">
              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mr-4">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Top-10 Global Bank</h3>
                    <p className="text-gray-600 text-sm">Supply Chain Finance</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  "Automated processing of supply chain finance applications with AI agents revolutionized our lending operations."
                </p>
              </div>
              <div className="border-t border-green-200 pt-4">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-green-600">95%</div>
                    <div className="text-xs text-gray-600">Faster processing</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">$2M+</div>
                    <div className="text-xs text-gray-600">Additional financing</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Case Study 3 */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-8">
              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mr-4">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Enterprise Insurance Provider</h3>
                    <p className="text-gray-600 text-sm">Risk Management Automation</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  "AI-powered risk assessment and automated underwriting reduced processing time by 85% while improving accuracy."
                </p>
              </div>
              <div className="border-t border-purple-200 pt-4">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-purple-600">85%</div>
                    <div className="text-xs text-gray-600">Faster underwriting</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-600">50%</div>
                    <div className="text-xs text-gray-600">Better accuracy</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Excellence */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Enterprise-Grade Technology
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Built for scale, security, and reliability with cutting-edge AI and blockchain technology
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h3 className="text-3xl font-bold text-white mb-6">Platform Architecture</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Zap className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">AI-Powered Autonomous Agents</h4>
                    <p className="text-gray-300">Intelligent agents make complex decisions without human intervention, managing unlimited transaction complexity.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Shield className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Blockchain-Native Operations</h4>
                    <p className="text-gray-300">All transactions executed through smart contracts with automatic compliance and immutable audit trails.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <TrendingUp className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Predictive Intelligence</h4>
                    <p className="text-gray-300">98% accurate forecasting enables proactive optimization across all business operations.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-gray-800 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-purple-400 mb-2">99.99%</div>
                <div className="text-white font-semibold mb-1">Uptime SLA</div>
                <div className="text-gray-400 text-sm">Global redundancy</div>
              </div>
              <div className="bg-gray-800 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-purple-400 mb-2">&lt; 50ms</div>
                <div className="text-white font-semibold mb-1">API Response</div>
                <div className="text-gray-400 text-sm">Real-time processing</div>
              </div>
              <div className="bg-gray-800 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-purple-400 mb-2">1M+</div>
                <div className="text-white font-semibold mb-1">Daily Transactions</div>
                <div className="text-gray-400 text-sm">Scalable architecture</div>
              </div>
              <div className="bg-gray-800 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-purple-400 mb-2">SOC 2</div>
                <div className="text-white font-semibold mb-1">Compliance</div>
                <div className="text-gray-400 text-sm">Enterprise security</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Timeline */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              15-Day Enterprise Deployment
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Rapid enterprise platform deployment with dedicated implementation teams
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {/* Phase 1 */}
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-white">1</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Days 1-5</h3>
                  <h4 className="text-lg font-semibold text-blue-600">Enterprise Foundation</h4>
                </div>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm">AI Agent Configuration</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm">System Integration</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm">Financial Services Setup</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm">Security Implementation</span>
                  </li>
                </ul>
              </div>

              {/* Phase 2 */}
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-white">2</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Days 6-10</h3>
                  <h4 className="text-lg font-semibold text-purple-600">Advanced Features</h4>
                </div>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm">Multi-Party Integration</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm">Industry Customization</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm">Analytics Calibration</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm">Workflow Optimization</span>
                  </li>
                </ul>
              </div>

              {/* Phase 3 */}
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-white">3</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Days 11-15</h3>
                  <h4 className="text-lg font-semibold text-green-600">Global Activation</h4>
                </div>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm">International Operations</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm">Advanced Automation</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm">Partner Network Integration</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm">Performance Optimization</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#854DFF] to-[#7A42E6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Enterprise?
          </h2>
          <p className="text-xl text-[#B19CFF] mb-8 max-w-3xl mx-auto">
            Experience the next generation of enterprise commerce platforms. Deploy in 15 days and see immediate results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-[#854DFF] hover:bg-gray-50 font-semibold text-lg px-8 py-4 rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
            >
              Schedule Enterprise Demo
              <ArrowRight className="ml-3 h-5 w-5" />
            </Link>
            <Link
              to="/pricing"
              className="border-2 border-white/30 text-white hover:bg-white/10 font-semibold text-lg px-8 py-4 rounded-lg transition-all duration-300 inline-flex items-center justify-center backdrop-blur-sm"
            >
              View Enterprise Pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
